import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SaveIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';

import { FormUnsavedConfirmationWrapper } from '../../../../components/pages/backoffice/form-unsaved-confirmation-wrapper.jsx';
import CancelButton from '../../../../components/buttons/cancel-button.jsx';
import Error from '../../../../components/feedback/error.jsx';
import Loading from '../../../../components/feedback/loading.jsx';
import Heading from '../../../../components/typography/heading.jsx';
import { Button } from '../../../../components/ui/button.jsx';
import { Checkbox } from '../../../../components/ui/checkbox.jsx';
import FormCard from '../../../../components/ui/form-card.jsx';
import FormFooter from '../../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/ui/form.jsx';
import { Input } from '../../../../components/ui/input.jsx';
import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { queryClient } from '../../../../services/query/client.js';

export function ShopsEdit() {
  const navigate = useNavigate();
  const { shopId } = useParams();

  const formEditShop = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      number: '',
      type: '',
      image: '',
      status: true,
    },
    resolver: yupResolver(
      yup.object().shape({
        name: yup
          .string()
          .min(4, 'Nome tem de ter pelo menos 4 caracteres')
          .max(80, 'Nome não pode ultrapassar 80 caracteres')
          .required('Nome é um campo obrigatório'),
        number: yup
          .number()
          .integer('Número inválido')
          .required('Número é um campo obrigatório'),
      }),
    ),
  });

  const queryShop = useQuery({
    queryKey: ['shop_edit'],
    queryFn: async () => {
      const { data: shop } = await msCatalystApi.get(`/shops/${shopId}`);

      formEditShop.reset({ ...shop.data });

      return shop.data;
    },
  });

  const mutationEditShop = useMutation({
    mutationFn: async (data) => {
      const { data: updatedConsumer } = await msCatalystApi.put(
        `/shops/${shopId}`,
        {
          ROWID: shopId,
          ...data,
        },
      );

      return updatedConsumer.data;
    },
    onSuccess: (data) => {
      toast.success('Loja editada com successo!');
      queryClient.setQueryData(['shop'], data);

      navigate(`/backoffice/admin/shops/${data.ROWID}`);
    },
    onError: (err) => {
      if (
        err?.response?.data?.message ===
        'Duplicate value for number. Please give a different value'
      ) {
        toast.error(`Já existe uma lojá com esse número`, {
          description: 'Por favor, altere o número de loja',
        });
      } else if (err?.response?.data) {
        toast.error(`Erro ao editar Loja: ${err.data.message}`, {
          description: 'Tente novamente mais tarde',
        });
      } else {
        toast.error('Erro ao editar Loja', {
          description: 'Tente novamente mais tarde',
        });
      }
    },
  });

  const onSubmit = async (data) => {
    mutationEditShop.mutate(data);
  };

  if (queryShop.isLoading) {
    return <Loading />;
  }

  if (queryShop.isError) {
    return <Error />;
  }

  return (
    <FormUnsavedConfirmationWrapper form={formEditShop}>
      <div className="pb-6">
        <Heading className="pb-2">Editar Loja</Heading>
        <h2 className="text-lg text-zinc-600 dark:text-zinc-400">
          {queryShop.data.name}
        </h2>
      </div>

      <Form {...formEditShop}>
        <form onSubmit={formEditShop.handleSubmit(onSubmit)}>
          <div className="flex flex-col space-y-10">
            <div className="flex flex-col gap-5">
              <FormCard>
                <FormField
                  control={formEditShop.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Nome</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditShop.control}
                  name="number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Número</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditShop.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoria</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditShop.control}
                  name="image"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Imagem URL</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditShop.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estado</FormLabel>
                      <div className="flex h-10 flex-row items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            disabled={formEditShop.formState.isSubmitting}
                            id="status"
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked)
                            }
                          />
                        </FormControl>
                        <FormLabel className="space-y-0">Ativa</FormLabel>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCard>
            </div>
          </div>

          <FormFooter>
            <CancelButton />

            <Button
              type="submit"
              disabled={
                formEditShop.formState.isSubmitting ||
                !formEditShop.formState.isDirty
              }
              isLoading={
                formEditShop.formState.isSubmitting || formEditShop.isPending
              }
            >
              <SaveIcon />
              Guardar Alterações
            </Button>
          </FormFooter>
        </form>
      </Form>
    </FormUnsavedConfirmationWrapper>
  );
}
